import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import ChangePasswordModal from "../../components/ChangePasswordModal";
import DashboardPageHeader from "../../components/Dashboard/DashboardPageHeader";
import { getProfile, updateUserProfile } from "../../services/apiService";
import { getStoredUser, isPartner } from "../../utils/auth";

const COUNTRIES = [
  "United Arab Emirates",
  "United States",
  "United Kingdom",
  "India",
  "Germany",
  "Singapore",
  "Canada",
  "Australia",
  "France",
  "Japan",
];

function normalizeUser(user, fallbackUser = null) {
  const fullName =
    user?.name || user?.fullName || user?.full_name || fallbackUser?.full_name || "";
  const nameParts = fullName.trim().split(/\s+/).filter(Boolean);

  const firstName =
    user?.firstName ||
    user?.first_name ||
    nameParts[0] ||
    fallbackUser?.firstName ||
    fallbackUser?.first_name ||
    "";
  const lastName =
    user?.lastName ||
    user?.last_name ||
    (nameParts.length > 1 ? nameParts.slice(1).join(" ") : "") ||
    fallbackUser?.lastName ||
    fallbackUser?.last_name ||
    "";

  return {
    firstName,
    lastName,
    email: user?.email || fallbackUser?.email || "",
    role: user?.role || user?.userRole || fallbackUser?.role || fallbackUser?.userRole || "",
    phone: user?.phone ?? fallbackUser?.phone ?? "",
    countryCode:
      user?.country_code ||
      user?.countryCode ||
      fallbackUser?.country_code ||
      fallbackUser?.countryCode ||
      "",
    country: user?.country ?? fallbackUser?.country ?? "",
    bio: user?.bio ?? fallbackUser?.bio ?? "",
    companyName:
      user?.company_name ||
      user?.companyName ||
      fallbackUser?.company_name ||
      fallbackUser?.companyName ||
      "",
  };
}

function composePhoneValue(countryCode, phoneNumber) {
  const codeDigits = String(countryCode || "").replace(/\D/g, "");
  const numberDigits = String(phoneNumber || "").replace(/\D/g, "");
  if (!codeDigits && !numberDigits) return "";
  return `${codeDigits}${numberDigits}`;
}

function parsePhoneValue(fullPhone) {
  const digits = String(fullPhone || "").replace(/\s/g, "");
  if (!digits) {
    return { countryCode: "", phone: "" };
  }

  const countryCode = "+" + (digits.match(/^\d{1,4}/)?.[0] || "971");
  const phone = digits.replace(/^\d{1,4}/, "");
  return { countryCode, phone };
}

function getUserStorage() {
  if (localStorage.getItem("user")) return localStorage;
  if (sessionStorage.getItem("user")) return sessionStorage;
  return localStorage;
}

function buildProfilePayload(profile) {
  return {
    first_name: profile.firstName,
    last_name: profile.lastName,
    phone: profile.phone,
    country_code: profile.countryCode,
    country: profile.country,
    bio: profile.bio,
  };
}

function parseProfileResponse(response, fallbackUser = null) {
  const data = response?.data ?? response;
  if (data?.statusCode != null && data?.data == null && !data?.email) {
    return normalizeUser(fallbackUser || {}, fallbackUser);
  }
  return normalizeUser(data, fallbackUser);
}

function syncStoredUser(profile) {
  const storage = getUserStorage();
  const existingUser = getStoredUser() || {};
  const displayName = `${profile.firstName} ${profile.lastName}`.trim();

  const updatedUser = {
    ...existingUser,
    first_name: profile.firstName,
    last_name: profile.lastName,
    firstName: profile.firstName,
    lastName: profile.lastName,
    name: displayName,
    phone: profile.phone,
    country_code: profile.countryCode,
    countryCode: profile.countryCode,
    country: profile.country,
    bio: profile.bio,
    company_name: profile.companyName,
    companyName: profile.companyName,
  };

  storage.setItem("user", JSON.stringify(updatedUser));
}

function Settings() {
  const { t } = useTranslation();
  const partnerView = isPartner();
  const storedUser = useMemo(() => getStoredUser(), []);
  const initialProfile = useMemo(() => normalizeUser(storedUser), [storedUser]);

  const [profile, setProfile] = useState(initialProfile);
  const [savedProfile, setSavedProfile] = useState(initialProfile);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [profileStatus, setProfileStatus] = useState({ type: null, message: "" });
  const [passwordStatus, setPasswordStatus] = useState({ type: null, message: "" });

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      setIsLoading(true);
      setProfileStatus({ type: null, message: "" });

      try {
        const response = await getProfile();
        if (cancelled) return;

        const nextProfile = parseProfileResponse(response, getStoredUser());
        syncStoredUser(nextProfile);
        setProfile(nextProfile);
        setSavedProfile(nextProfile);
      } catch (error) {
        if (cancelled) return;

        setProfileStatus({
          type: "error",
          message:
            error?.response?.data?.message || t("dashboard.settings.loadFailed"),
        });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, []);

  const displayName = `${profile.firstName} ${profile.lastName}`.trim();
  const phoneValue = useMemo(
    () => composePhoneValue(profile.countryCode, profile.phone),
    [profile.countryCode, profile.phone]
  );
  const isDirty = JSON.stringify(profile) !== JSON.stringify(savedProfile);

  const updateProfile = (field, value) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
    setProfileStatus({ type: null, message: "" });
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    setProfileStatus({ type: null, message: "" });
  };

  const handlePhoneChange = (value) => {
    const { countryCode, phone } = parsePhoneValue(value);
    setProfile((prev) => ({ ...prev, countryCode, phone }));
    setProfileStatus({ type: null, message: "" });
  };

  const handleDiscard = () => {
    setProfile(savedProfile);
    setProfileStatus({ type: null, message: "" });
    setIsEditing(false);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setProfileStatus({ type: null, message: "" });
    setIsSaving(true);

    try {
      await updateUserProfile(buildProfilePayload(profile));

      const response = await getProfile();
      const nextProfile = parseProfileResponse(response, getStoredUser());

      syncStoredUser(nextProfile);
      setProfile(nextProfile);
      setSavedProfile(nextProfile);
      setIsEditing(false);

      setProfileStatus({
        type: "success",
        message: t("dashboard.settings.saveSuccess"),
      });
    } catch (error) {
      setProfileStatus({
        type: "error",
        message:
          error?.response?.data?.message || t("dashboard.settings.saveFailed"),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordChanged = () => {
    setPasswordStatus({
      type: "success",
      message: t("dashboard.settings.passwordUpdated"),
    });
  };

  return (
    <div className="dashboard-page settings-page">
      <DashboardPageHeader title={t("dashboard.sidebar.settings")} />
      <div className="settings-form">
        <form className="dashboard-card settings-card" onSubmit={handleSave}>
          <h2 className="settings-card__title">{t("dashboard.settings.personalInfo")}</h2>

          <div className="settings-profile">
            <div className="settings-profile__details">
              <p className="settings-profile__name">{displayName}</p>
              <p className="settings-profile__meta">
                {profile.email} · {profile.role}
              </p>
            </div>
          </div>

          <div className="settings-fields settings-fields--2">
            <div className="settings-field">
              <label htmlFor="settings-first-name">{t("dashboard.settings.firstName")}</label>
              <input
                id="settings-first-name"
                type="text"
                value={profile.firstName}
                readOnly={!isEditing || isLoading}
                disabled={!isEditing || isLoading}
                onChange={(event) => updateProfile("firstName", event.target.value)}
              />
            </div>
            <div className="settings-field">
              <label htmlFor="settings-last-name">{t("dashboard.settings.lastName")}</label>
              <input
                id="settings-last-name"
                type="text"
                value={profile.lastName}
                readOnly={!isEditing || isLoading}
                disabled={!isEditing || isLoading}
                onChange={(event) => updateProfile("lastName", event.target.value)}
              />
            </div>
          </div>

          <div className="settings-field">
            <label htmlFor="settings-email">{t("dashboard.settings.emailAddress")}</label>
            <input
              id="settings-email"
              type="email"
              value={profile.email}
              readOnly
              disabled
            />
          </div>

          {partnerView && profile.companyName ? (
            <div className="settings-field">
              <label htmlFor="settings-company">{t("dashboard.settings.companyName")}</label>
              <input
                id="settings-company"
                type="text"
                value={profile.companyName}
                readOnly
                disabled
              />
            </div>
          ) : null}

          <div className="settings-field">
            <label htmlFor="settings-phone">{t("dashboard.settings.phoneNumber")}</label>
            <PhoneInput
              country="ae"
              value={phoneValue}
              onChange={handlePhoneChange}
              disabled={!isEditing || isLoading}
              inputProps={{
                id: "settings-phone",
                name: "phoneNumber",
              }}
              inputClass="settings-phone-input"
              containerClass="phone-input-container"
              buttonClass="country-dropdown-button"
              dropdownClass="country-dropdown"
              searchClass="country-search"
              enableSearch
              disableSearchIcon
              searchPlaceholder={t("common.searchCountry")}
              placeholder={t("common.enterPhone")}
            />
          </div>

          <div className="settings-field">
            <label htmlFor="settings-country">{t("dashboard.settings.country")}</label>
            <select
              id="settings-country"
              value={profile.country}
              disabled={!isEditing || isLoading}
              onChange={(event) => updateProfile("country", event.target.value)}
            >
              <option value="">{t("dashboard.settings.selectCountry")}</option>
              {COUNTRIES.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </div>

          <div className="settings-field">
            <label htmlFor="settings-bio">{t("dashboard.settings.bioOptional")}</label>
            <textarea
              id="settings-bio"
              rows={4}
              value={profile.bio}
              placeholder={t("dashboard.settings.bioPlaceholder")}
              readOnly={!isEditing || isLoading}
              disabled={!isEditing || isLoading}
              onChange={(event) => updateProfile("bio", event.target.value)}
            />
          </div>

          {profileStatus.message ? (
            <p
              className={`settings-form-status${
                profileStatus.type ? ` settings-form-status--${profileStatus.type}` : ""
              }`}
              role="status"
            >
              {profileStatus.message}
            </p>
          ) : null}

          <div className="settings-actions">
            {!isEditing ? (
              <button
                type="button"
                className="settings-btn settings-btn--primary"
                onClick={handleStartEdit}
                disabled={isLoading}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {t("dashboard.settings.edit")}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="settings-btn settings-btn--outline"
                  onClick={handleDiscard}
                >
                  {t("dashboard.settings.discard")}
                </button>
                <button
                  type="submit"
                  className="settings-btn settings-btn--primary"
                  disabled={!isDirty || isSaving}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {t("dashboard.settings.saveChanges")}
                </button>
              </>
            )}
          </div>
        </form>

        <section className="dashboard-card settings-card">
          <div className="settings-card__header">
            <h2 className="settings-card__title">{t("dashboard.settings.password")}</h2>
            <p className="settings-card__subtitle">{t("dashboard.settings.passwordSubtitle")}</p>
          </div>

          <button
            type="button"
            className="settings-btn settings-btn--outline"
            onClick={() => setIsPasswordModalOpen(true)}
          >
            {t("dashboard.settings.changePassword")}
          </button>

          {passwordStatus.message ? (
            <p
              className={`settings-form-status settings-card__status${
                passwordStatus.type ? ` settings-form-status--${passwordStatus.type}` : ""
              }`}
              role="status"
            >
              {passwordStatus.message}
            </p>
          ) : null}
        </section>
      </div>

      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onSuccess={handlePasswordChanged}
      />
    </div>
  );
}

export default Settings;
